import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../utils/api';

type LineItem = {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
};

type InvoiceData = {
  id: string;
  invoiceNumber: string;
  status: 'DRAFT' | 'SENT' | 'VIEWED' | 'PAID' | 'OVERDUE' | 'VOIDED' | 'CANCELLED';
  issuedAt: string | null;
  dueDate: string;
  paidDate: string | null;
  amount: number;
  vatAmount: number;
  vatRate: number;
  totalAmount: number;
  notes?: string | null;
  terms?: string | null;
  items: LineItem[];
  payUrl?: string | null;
  client: {
    name: string;
    email?: string | null;
    phone?: string | null;
    address?: string | null;
    postcode?: string | null;
  } | null;
  company: {
    name?: string | null;
    address?: string | null;
    phone?: string | null;
    businessEmail?: string | null;
    logo?: string | null;
    vatNumber?: string | null;
    gasRegistrationNumber?: string | null;
  } | null;
  job: {
    title: string | null;
    address: string | null;
    postcode: string | null;
    completedDate: string | null;
  } | null;
};

type View = 'loading' | 'ready' | 'paid' | 'error' | 'notfound';

const money = (n: number | undefined) => `£${Number(n || 0).toFixed(2)}`;
const formatDate = (iso: string | null | undefined) => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};
const isPastDue = (dueDate: string, status: string) =>
  status !== 'PAID' && status !== 'VOIDED' && status !== 'CANCELLED' && new Date(dueDate).getTime() < Date.now();

export default function PublicInvoice() {
  const { token = '' } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const justPaid = searchParams.get('paid') === '1';

  const [view, setView] = useState<View>('loading');
  const [data, setData] = useState<InvoiceData | null>(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [paying, setPaying] = useState(false);

  const pdfUrl = useMemo(() => `${API_BASE_URL}/public/invoices/${token}/pdf`, [token]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/public/invoices/${token}`);
        if (res.status === 404) {
          if (!cancelled) setView('notfound');
          return;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (cancelled) return;
        setData(json.data);
        setView(json.data?.status === 'PAID' ? 'paid' : 'ready');
      } catch (err: any) {
        if (cancelled) return;
        setErrorMsg(err?.message || 'Failed to load invoice');
        setView('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handlePay = async () => {
    setPaying(true);
    try {
      const res = await fetch(`${API_BASE_URL}/public/invoices/${token}/pay-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `HTTP ${res.status}`);
      }
      const json = await res.json();
      if (json.data?.alreadyPaid) {
        setView('paid');
        setPaying(false);
        return;
      }
      if (json.data?.url) {
        window.location.href = json.data.url;
        return;
      }
      throw new Error('No payment URL returned');
    } catch (err: any) {
      alert(`Payment could not be started: ${err.message}`);
      setPaying(false);
    }
  };

  if (view === 'loading') {
    return <Centered><p>Loading your invoice…</p></Centered>;
  }
  if (view === 'notfound') {
    return <Centered><h1>Invoice not found</h1><p>This link may have expired or been revoked. Please contact the sender.</p></Centered>;
  }
  if (view === 'error') {
    return <Centered><h1>Something went wrong</h1><p>{errorMsg}</p></Centered>;
  }
  if (!data) return null;

  const { client, company, job, items, status } = data;
  const companyName = company?.name || 'Your contractor';
  const overdue = isPastDue(data.dueDate, status);

  return (
    <div style={styles.page}>
      <div style={styles.wrap}>
        {/* Header */}
        <header style={styles.header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {company?.logo ? (
              <img src={company.logo} alt={companyName} style={{ height: 40, width: 40, objectFit: 'contain', borderRadius: 6 }} />
            ) : null}
            <div>
              <div style={{ fontWeight: 700, fontSize: 16 }}>{companyName}</div>
              {company?.businessEmail && <div style={{ color: '#6b7280', fontSize: 12 }}>{company.businessEmail}</div>}
            </div>
          </div>
          <StatusBadge status={status} overdue={overdue} />
        </header>

        {/* Paid success banner */}
        {justPaid && status === 'PAID' && (
          <Banner tone="success" title="Payment received — thanks!" body={`Your payment to ${companyName} was successful.`} />
        )}

        {/* Summary card */}
        <section style={styles.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
            <div>
              <div style={styles.label}>Invoice</div>
              <h1 style={{ margin: '4px 0 0', fontSize: 28, color: '#111827' }}>{data.invoiceNumber}</h1>
              {job?.title && <div style={{ color: '#374151', marginTop: 6 }}>{job.title}</div>}
              <div style={{ color: '#6b7280', fontSize: 13, marginTop: 10 }}>
                Issued {formatDate(data.issuedAt)} · Due {formatDate(data.dueDate)}
                {data.paidDate ? ` · Paid ${formatDate(data.paidDate)}` : ''}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={styles.label}>{status === 'PAID' ? 'Amount paid' : 'Amount due'}</div>
              <div style={{ fontSize: 32, fontWeight: 700, color: '#111827', marginTop: 4 }}>{money(data.totalAmount)}</div>
              <div style={{ color: '#6b7280', fontSize: 13 }}>
                Net {money(data.amount)} · VAT {money(data.vatAmount)} ({Math.round(data.vatRate)}%)
              </div>
            </div>
          </div>

          {(client || job) && (
            <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #e5e7eb', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
              {client && (
                <div>
                  <div style={styles.label}>Billed to</div>
                  <div style={{ fontWeight: 600, marginTop: 4 }}>{client.name}</div>
                  {client.address && <div style={{ color: '#6b7280', fontSize: 13 }}>{client.address}</div>}
                  {client.postcode && <div style={{ color: '#6b7280', fontSize: 13 }}>{client.postcode}</div>}
                </div>
              )}
              {(job?.address || job?.postcode) && (
                <div>
                  <div style={styles.label}>Work carried out at</div>
                  <div style={{ marginTop: 4 }}>{job?.address}</div>
                  <div style={{ color: '#6b7280', fontSize: 13 }}>{job?.postcode}</div>
                  {job?.completedDate && <div style={{ color: '#6b7280', fontSize: 13, marginTop: 4 }}>Completed {formatDate(job.completedDate)}</div>}
                </div>
              )}
            </div>
          )}
        </section>

        {/* Pay CTA */}
        {status !== 'PAID' && status !== 'VOIDED' && status !== 'CANCELLED' && (
          <section style={{ ...styles.card, background: overdue ? '#fef2f2' : '#fff7ed', border: `1px solid ${overdue ? '#fecaca' : '#fed7aa'}` }}>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: overdue ? '#991b1b' : '#9a3412' }}>
                  {overdue ? 'This invoice is overdue' : 'Ready when you are'}
                </div>
                <div style={{ color: '#6b7280', marginTop: 4 }}>
                  Pay securely by card. No account required.
                </div>
              </div>
              <button
                onClick={handlePay}
                disabled={paying}
                style={{ ...styles.primaryBtn, fontSize: 16, padding: '14px 28px', background: '#F57D29' }}
              >
                {paying ? 'Starting secure payment…' : `Pay ${money(data.totalAmount)}`}
              </button>
            </div>
          </section>
        )}

        {/* Line items */}
        {items?.length > 0 && (
          <section style={styles.card}>
            <div style={styles.label}>Line items</div>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Description</th>
                  <th style={{ ...styles.th, textAlign: 'right', width: 60 }}>Qty</th>
                  <th style={{ ...styles.th, textAlign: 'right', width: 100 }}>Unit</th>
                  <th style={{ ...styles.th, textAlign: 'right', width: 100 }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id}>
                    <td style={styles.td}>{it.description}</td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>{it.quantity}</td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>{money(it.unitPrice)}</td>
                    <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600 }}>{money(it.totalPrice)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {/* Notes / Terms */}
        {data.notes && (
          <section style={styles.card}>
            <div style={styles.label}>Notes</div>
            <p style={{ margin: '8px 0 0', whiteSpace: 'pre-wrap', color: '#374151' }}>{data.notes}</p>
          </section>
        )}
        {data.terms && (
          <section style={styles.card}>
            <div style={styles.label}>Payment terms</div>
            <p style={{ margin: '8px 0 0', whiteSpace: 'pre-wrap', color: '#6b7280' }}>{data.terms}</p>
          </section>
        )}

        {/* PDF */}
        <section style={styles.card}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
            <a href={pdfUrl} target="_blank" rel="noreferrer" style={styles.secondaryBtn}>
              View PDF
            </a>
            {company?.gasRegistrationNumber && (
              <div style={{ color: '#9ca3af', fontSize: 12 }}>Gas Safe {company.gasRegistrationNumber}</div>
            )}
          </div>
        </section>

        <footer style={{ textAlign: 'center', color: '#9ca3af', fontSize: 12, padding: '24px 0' }}>
          Sent securely via Certs · {companyName}
        </footer>
      </div>
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#f3f4f6', padding: 24, fontFamily: systemFont }}>
      <div style={{ background: '#fff', padding: 32, borderRadius: 12, maxWidth: 420, textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>{children}</div>
    </div>
  );
}

function StatusBadge({ status, overdue }: { status: InvoiceData['status']; overdue: boolean }) {
  const effective = overdue ? 'OVERDUE' : status;
  const map: Record<string, { bg: string; fg: string; label: string }> = {
    SENT: { bg: '#dbeafe', fg: '#1e40af', label: 'Awaiting payment' },
    VIEWED: { bg: '#dbeafe', fg: '#1e40af', label: 'Awaiting payment' },
    PAID: { bg: '#d1fae5', fg: '#065f46', label: 'Paid' },
    OVERDUE: { bg: '#fee2e2', fg: '#991b1b', label: 'Overdue' },
    VOIDED: { bg: '#e5e7eb', fg: '#374151', label: 'Voided' },
    CANCELLED: { bg: '#e5e7eb', fg: '#374151', label: 'Cancelled' },
    DRAFT: { bg: '#e5e7eb', fg: '#374151', label: 'Draft' },
  };
  const s = map[effective] || map.SENT;
  return (
    <span style={{ background: s.bg, color: s.fg, padding: '6px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600 }}>
      {s.label}
    </span>
  );
}

function Banner({ tone, title, body }: { tone: 'success' | 'muted' | 'info'; title: string; body: string }) {
  const colors = {
    success: { bg: '#ecfdf5', border: '#a7f3d0', fg: '#065f46' },
    muted: { bg: '#f9fafb', border: '#e5e7eb', fg: '#374151' },
    info: { bg: '#eff6ff', border: '#bfdbfe', fg: '#1e40af' },
  }[tone];
  return (
    <div style={{ background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 10, padding: 16, color: colors.fg }}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>{title}</div>
      <div>{body}</div>
    </div>
  );
}

const systemFont = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

const styles: Record<string, React.CSSProperties> = {
  page: { background: '#f3f4f6', minHeight: '100vh', padding: '24px 16px 64px', fontFamily: systemFont, color: '#111827' },
  wrap: { maxWidth: 760, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 16 },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 4px' },
  card: { background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  label: { fontSize: 11, fontWeight: 700, letterSpacing: 0.6, color: '#6b7280', textTransform: 'uppercase' },
  table: { width: '100%', borderCollapse: 'collapse', marginTop: 10, fontSize: 14 },
  th: { textAlign: 'left', padding: '10px 8px', borderBottom: '1px solid #e5e7eb', fontSize: 12, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase' },
  td: { padding: '10px 8px', borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' },
  primaryBtn: { background: '#1d4ed8', color: '#fff', border: 'none', padding: '10px 18px', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 14 },
  secondaryBtn: { background: '#fff', color: '#111827', border: '1px solid #d1d5db', padding: '10px 18px', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 14, textDecoration: 'none', display: 'inline-block' },
};
